import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { apiGet, apiPatch, apiPost, ApiError } from "@/lib/api";
import KybDocBlock, { KybDoc, KybDocType } from "@/components/KybDocBlock";

type KybDbStatus = "approved" | "pending" | "rejected";

type KybData = {
  id: number | string;
  // identity from BusinessKybData (read-only)
  company_name: string;
  registration_number: string;
  incorporation_country: string;
  // user-editable
  website_url?: string | null;
  business_activity: string;
  status: KybDbStatus;
  documents?: KybDoc[];
};

const ORDER: KybDocType[] = [
  "certificate",
  "articles",
  "shareholders",
  "address_proof",
  "vat_certificate",
  "business_license",
];

export default function KybEdit() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [row, setRow] = useState<KybData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    // readonly fields will be set from fetched row and rendered as disabled
    company_name: "",
    registration_number: "",
    incorporation_country: "",
    // editable
    website_url: "",
    business_activity: "",
  });

  // Files picked for reupload (only allowed for rejected/missing)
  const [files, setFiles] = useState<Partial<Record<KybDocType, File | null>>>(
    {}
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiGet<KybData>(`/xapi/kyb/${id}`);
        if (cancelled) return;
        setRow(data);
        setForm({
          company_name: data.company_name ?? "",
          registration_number: data.registration_number ?? "",
          incorporation_country: data.incorporation_country ?? "",
          website_url: data.website_url ?? "",
          business_activity: data.business_activity ?? "",
        });
      } catch (e: unknown) {
        const msg =
          e instanceof ApiError ? e.message : "Failed to load KYB.";
        if (!cancelled) setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const docMap: Record<KybDocType, KybDoc | undefined> = useMemo(() => {
    const m: Record<KybDocType, KybDoc | undefined> = {
      certificate: undefined,
      articles: undefined,
      shareholders: undefined,
      address_proof: undefined,
      vat_certificate: undefined,
      business_license: undefined,
    };
    (row?.documents ?? []).forEach((d) => {
      const key = (d.type as KybDocType) || "certificate";
      m[key] = d;
    });
    return m;
  }, [row]);

  function allowUploadFor(t: KybDocType) {
    const s = docMap[t]?.status ?? "missing";
    return s === "rejected" || s === "missing";
  }

  function update<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      // 1) Update only editable KYB details
      await apiPatch(`/xapi/kyb/${id}`, {
        website_url: form.website_url || null,
        business_activity: form.business_activity,
      });

      // 2) Upload selected files (only those provided)
      const picked = Object.entries(files).filter(
        ([, f]) => !!f
      ) as [KybDocType, File][];

      if (picked.length) {
        const fd = new FormData();
        picked.forEach(([t, f]) => fd.append(`documents[${t}]`, f));
        await apiPost("/xapi/kyb/documents", fd);
      }

      nav("/kyb/status");
    } catch (e: unknown) {
      if (e instanceof ApiError && (e as ApiError).validation) {
        const first = Object.values((e as ApiError).validation!)[0]?.[0];
        setError(first || (e as any).message);
      } else {
        setError((e as any)?.message || "Failed to update KYB.");
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen p-6">
        <div className="max-w-3xl mx-auto space-y-4">
          <div className="section-box skeleton h-10 rounded-xl" />
          <div className="card-box skeleton h-64 rounded-xl" />
          <div className="card-box skeleton h-64 rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen p-6">
        <div className="max-w-3xl mx-auto">
          <div className="alert-danger rounded-xl px-4 py-3">{error}</div>
          <div className="mt-4">
            <Link to="/kyb/status" className="btn btn-white">
              Back to KYB Status
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!row) {
    return (
      <div className="min-h-screen p-6">
        <div className="max-w-3xl mx-auto">
          <div className="alert-info rounded-xl px-4 py-3">Not found.</div>
          <div className="mt-4">
            <Link to="/kyb/status" className="btn btn-white">
              Back to KYB Status
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen p-6"
      // Ensure bottom buttons are never hidden by the mobile tab bar
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 120px)" }}
    >
      <div className="max-w-3xl mx-auto">
        <header className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold">Edit KYB</h1>
          <Link to="/kyb/status" className="btn btn-white">
            Back to KYB Status
          </Link>
        </header>

        {error && (
          <div className="alert-danger rounded-xl px-4 py-3 mb-4">{error}</div>
        )}

        <form onSubmit={submit} className="space-y-6">
          {/* Company fields (read-only) */}
          <div className="card-box space-y-4 p-4 sm:p-6">
            <label className="block">
              <span className="block font-medium">Company Name</span>
              <input
                className="w-full border rounded px-3 py-2 bg-gray-100"
                value={form.company_name}
                readOnly
                aria-readonly
              />
            </label>

            <label className="block">
              <span className="block font-medium">Registration Number</span>
              <input
                className="w-full border rounded px-3 py-2 bg-gray-100"
                value={form.registration_number}
                readOnly
                aria-readonly
              />
            </label>

            <label className="block">
              <span className="block font-medium">Country of Incorporation</span>
              <input
                className="w-full border rounded px-3 py-2 bg-gray-100"
                value={form.incorporation_country}
                readOnly
                aria-readonly
              />
            </label>

            <label className="block">
              <span className="block font-medium">Business Activity</span>
              <input
                className="w-full border rounded px-3 py-2"
                value={form.business_activity}
                onChange={(e) => update("business_activity", e.target.value)}
                required
              />
            </label>

            <label className="block">
              <span className="block font-medium">Website URL</span>
              <input
                type="url"
                className="w-full border rounded px-3 py-2"
                value={form.website_url}
                onChange={(e) => update("website_url", e.target.value)}
                placeholder="https://example.com"
              />
            </label>
          </div>

          {/* Documents with conditional inputs */}
          <div className="card-box p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Documents</h3>
              {row.status !== "approved" && (
                <span className="chip">Reupload rejected/missing only</span>
              )}
            </div>

            <div className="space-y-3">
              {ORDER.map((t) => (
                <KybDocBlock
                  key={t}
                  docType={t}
                  doc={docMap[t] || ({ type: t, status: "missing" } as KybDoc)}
                  allowUpload={allowUploadFor(t)}
                  onPick={(type, file) =>
                    setFiles((m) => ({ ...m, [type]: file }))
                  }
                />
              ))}
            </div>
          </div>

          {/* Action row (static, end of content) */}
          <div className="flex items-center gap-4">
            <button
              type="submit"
              disabled={saving}
              className="btn disabled:opacity-60"
            >
              {saving ? "Saving…" : "Update KYB"}
            </button>
            <Link to="/kyb/status" className="btn btn-white">
              Back to KYB Status
            </Link>
          </div>
          {/* /Action row */}
        </form>
      </div>
    </div>
  );
}
